import { useTranslation } from "react-i18next";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import DropDownListComponent from "@CubeComponents/form/DropdownListComponent";
import TextBoxComponent from "@CubeComponents/form/TextBoxComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { spacesDataService, workflowsService } from "../../services/dataManager";
import { Query } from "@syncfusion/ej2-data";
import css from "./Reviews.module.css";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchWorkflowLevels } from "@Components/SpaceSettings/UserAdministration/Workflows/WorkflowHelper";
import { showToastNotification } from "../../actions/toast";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "../../services/constants";
import { createNewReview, getResourceDetails } from "./ReviewsHelper";
import { useDispatch } from "react-redux";
import FileManagerAsPopup from "@Components/FileManager/FileManagerAsPopup";
import ReviewLevelTree from "@Components/SpaceSettings/UserAdministration/Workflows/WorkflowReviewLevels/ReviewLevelTree";
import { defaultCreateReviewData } from "./ReviewsConstants";
import TransitionsDiagram from "@Components/SpaceSettings/UserAdministration/Workflows/WorkflowTransitions/TransitionsDiagram";
import RadioButtonComponent from "../../cubeComponent/form/RadioButtonComponent";
import Issues from "../Issues/Issues";
import spacesIcon from "../../assets/icons/svg/spaces_icon.svg";
import { issuesInFilesCount } from "../../actions/bcfData";
import ChecklistAssignmentModal from "./ChecklistAssignmentModal";
import { Autocomplete, TextField } from "@mui/material";
import Text from "../../cubeComponent/html/Text/Text";

interface ReviewDataType {
  name: string;
  description: string;
  workflow: {
    id: string | null;
    reviewLevels: any[];
    checklists: any[];
  };
  resources: any[];
  hasIssues: boolean;
  stage: number;
  attachIssues?: boolean;
}

interface CreateReviewModalProps {
  closeModal: () => void;
  refreshGrid: () => void;
  spaceId?: string;
  projectId: string;
  initialFiles?: any[];
}

const CreateReviewModal: React.FC<CreateReviewModalProps> = ({
  closeModal,
  refreshGrid,
  spaceId = "",
  projectId,
  initialFiles = [],
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const spaceRef = useRef<any>(null);

  const [reviewData, setReviewData] = useState<ReviewDataType>({
    ...defaultCreateReviewData,
    workflow: {
      id: defaultCreateReviewData?.workflow?.id ?? null,
      reviewLevels: defaultCreateReviewData?.workflow?.reviewLevels ?? [],
      checklists: defaultCreateReviewData?.workflow?.checklists ?? [],
    },
    hasIssues: false,
  });

  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("levels");
  const [defaultSpaceID, setDefaultSpaceID] = useState<string>(spaceId);
  const [isLoadingCreateButton, setIsLoadingCreateButton] = useState<boolean>(false);
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<any>(null);

  const spaceQuery = new Query().addParams("projectId", projectId);

  const closeReviewCreation = () => closeModal();

  const fetchWorkflows = async () => {
    if (!defaultSpaceID) return;

    const workflowsQuery = new Query()
      .addParams("spaceId", defaultSpaceID)
      .addParams("filterByInitiator", "true")
      .where("archivedStatus", "equal", 0);

    const result: any = await workflowsService.executeQuery(workflowsQuery);
    setWorkflows(result.result || []);
  };

  useEffect(() => {
    fetchWorkflows();
  }, [defaultSpaceID, reviewData.stage]);

  const handleToastNotification = (data: any) => {
    if (data.status) {
      dispatch(
        showToastNotification({
          content: t("The review has been created successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: t("The review could not be created"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const attachIssuesRadioBtnCallback = (args: boolean) => {
    setReviewData((prev: any) => ({ ...prev, attachIssues: args }));
  };

  const onDataBound = (event: any) => {
    const dataSource = event.items;
    if (!spaceId && dataSource.length > 0 && !spaceRef.current.value) {
      setDefaultSpaceID(dataSource[0].spaceId);
      spaceRef.current.value = dataSource[0].spaceId;
    }
  };

  const resourceSelectionCallback = async (listOfFiles: any) => {
    const resources = Object.values(listOfFiles);
    const resourcesMap = getResourceDetails(resources);

    if (!resourcesMap.length) {
      dispatch(showToastNotification({ content: "Please add at least one file", type: TOAST_TYPE_ERROR }));
      setReviewData((prev: any) => ({ ...prev, stage: 1 }));
      return;
    }

    const fileIds = resourcesMap
      .filter(
        ({ resourceType }: any) => resourceType === "RESOURCE_TYPE_FILE" || resourceType === "RESOURCE_TYPE_EMBED",
      )
      .map((resource: any) => resource.fileVersionId);

    const data = { value: { params: { fileId: fileIds }, skip: 0, take: 1 } };

    let containsIssues = false;
    try {
      containsIssues = !!(await issuesInFilesCount(data));
    } catch {
      containsIssues = false;
    }

    const hasChecklist =
      Array.isArray(reviewData.workflow?.checklists) &&
      reviewData.workflow.checklists.length > 0 &&
      Array.isArray(reviewData.workflow?.reviewLevels) &&
      reviewData.workflow.reviewLevels.length > 0;

    if (hasChecklist) {
      setIsLoadingCreateButton(false);
      setReviewData((prev: any) => ({
        ...prev,
        resources: resourcesMap,
        hasIssues: containsIssues,
        stage: 3,
      }));
    } else if (containsIssues) {
      setIsLoadingCreateButton(false);
      setReviewData((prev: any) => ({
        ...prev,
        resources: resourcesMap,
        hasIssues: true,
        stage: 4,
      }));
    } else {
      setIsLoadingCreateButton(true);
      await createReview(resourcesMap);
    }
  };

  const createReview = async (resourcesMap?: any) => {
    const { name, description, workflow, resources } = reviewData;
    const workflowId = workflow.id;
    const data = {
      name,
      spaceId: defaultSpaceID,
      workflowId,
      description,
      resources: resourcesMap ?? resources,
    };

    const response = await createNewReview(data);
    setIsLoadingCreateButton(false);
    handleToastNotification(response.data);
    refreshGrid();
    closeModal();
  };

  const moveToPreviousSection = () => {
    setReviewData((prev: any) => ({ ...prev, stage: 1 }));
  };

  const moveToNextSection = () => {
    if (initialFiles && initialFiles.length > 0) {
      const resourcesMap = getResourceDetails(initialFiles);

      const hasChecklist = reviewData.workflow.checklists;
      if (hasChecklist) {
        setReviewData((prev: any) => ({ ...prev, resources: resourcesMap, stage: 3 }));
      } else {
        setIsLoadingCreateButton(true);
        createReview(resourcesMap);
      }
    } else {
      setReviewData((prev: any) => ({ ...prev, stage: 2 }));
    }
  };

  const fetchWorkflowLevelDetails = async (workflowId: string) => {
    const res = await fetchWorkflowLevels({
      value: { skip: 0, take: 1000, params: { workflowId } },
    });
    return res.data.result;
  };

  const changeWorkflowHandler = async (event: any, newValue: any) => {
    if (!newValue) {
      setSelectedWorkflow(null);
      setReviewData((prev: any) => ({
        ...prev,
        workflow: {
          id: null,
          reviewLevels: [],
          checklists: [],
        },
      }));
      return;
    }

    setIsDataLoading(true);
    setSelectedWorkflow(newValue);
    const workflowLevelDetails = await fetchWorkflowLevelDetails(newValue._id);
    setReviewData((prev: any) => ({
      ...prev,
      workflow: {
        id: newValue._id,
        reviewLevels: workflowLevelDetails,
        checklists: workflowLevelDetails[0]?.checklists || [],
      },
    }));
    setIsDataLoading(false);
  };

  const changeReviewSpaceHandler = async (e: any) => {
    setDefaultSpaceID(e.value);
    setSelectedWorkflow(null);
    setReviewData((prev: any) => ({ ...prev, workflow: { ...prev.workflow, id: null } }));
  };

  const handleTabSwitch = (e: any) => {
    const target = e.currentTarget.attributes["data-name"].value;
    setActiveTab(target);
  };

  const confirmAttachIssuesCallback = () => {
    if (!reviewData.attachIssues) {
      setIsLoadingCreateButton(true);
      createReview();
      return;
    }
    setReviewData((prev: any) => ({ ...prev, stage: 5 }));
  };

  const issueSelectionCallback = (issues: any[]) => {
    const issuesListForReview = issues.map((issue: any) => ({
      resourceType: "RESOURCE_TYPE_ISSUE",
      issueId: issue.id,
    }));

    setReviewData((prev: any) => {
      const resourcesExceptIssues = prev.resources.filter(
        ({ resourceType }: any) => resourceType !== "RESOURCE_TYPE_ISSUE",
      );
      return { ...prev, resources: [...resourcesExceptIssues, ...issuesListForReview] };
    });
  };

  const attachIssuesToReviewCallback = () => {
    setIsLoadingCreateButton(true);
    createReview();
  };

  const SpacesDropDownList = useCallback(
    () => (
      <DropDownListComponent
        ref={spaceRef}
        dataSource={spacesDataService}
        query={spaceQuery}
        fields={{ text: "spaceName", value: "_id" }}
        change={changeReviewSpaceHandler}
        dataBound={onDataBound}
        enabled={!spaceId}
        value={spaceId}
      />
    ),
    [],
  );

  const header = () => (
    <div className="flex items-center justify-between w-full rounded-t-lg">
      <p className="text-2xl font-bold">
        <Text>{t("Create Review")}</Text>
      </p>
      <div className="flex items-center space-inline-2">
        <img src={spacesIcon} alt="" />
        <SpacesDropDownList />
      </div>
    </div>
  );

  const content = () => (
    <div className={css.createReviewContent}>
      <div className={css.createReviewContentWrapper}>
        <div className={css.flexRow}>
          <p className={css.labelHeading}>
            <Text>{t("Review Name")}</Text>
          </p>
          <div className={css.dropdownWrapper}>
            <TextBoxComponent
              data-name="name"
              placeholder={t("Enter Review Name")}
              onChange={(e: any) => setReviewData((prev: any) => ({ ...prev, name: e.value }))}
              value={reviewData.name}
            />
          </div>
        </div>

        <div className={css.flexRow}>
          <p className={css.labelHeading}>
            <Text>{t("Review Description")}</Text>
          </p>
          <div className={css.dropdownWrapper}>
            <TextBoxComponent
              data-name="description"
              onChange={(e: any) => setReviewData((prev: any) => ({ ...prev, description: e.value }))}
              placeholder={t("Enter Review Description")}
              value={reviewData.description}
            />
          </div>
        </div>

        <div className={css.flexRow}>
          <p className={css.labelHeading}>
            <Text>{t("Select Review Flow")}</Text>
          </p>
          <div className={css.dropdownWrapper}>
            <Autocomplete
              options={workflows}
              value={selectedWorkflow}
              onChange={changeWorkflowHandler}
              getOptionLabel={(option: any) => option.name}
              isOptionEqualToValue={(option: any, value: any) => option._id === value._id}
              renderInput={(params) => <TextField {...params} placeholder={t("Select Review Flow")} />}
            />
          </div>
        </div>
      </div>

      <div className={css.flexCol}>
        <div className={css.workflowDetailsHeader}>
          <button data-name="levels" className={css.individualTab} onClick={handleTabSwitch} type="button">
            <span>
              <Text>{t("Review Levels")}</Text>
            </span>
            <div className={activeTab === "levels" ? css.selectedTab : css.unselectedTab} />
          </button>
          <button data-name="diagram" className={css.individualTab} onClick={handleTabSwitch} type="button">
            <span>
              <Text>{t("Transition Diagram")}</Text>
            </span>
            <div className={activeTab === "diagram" ? css.selectedTab : css.unselectedTab} />
          </button>
        </div>

        <div className={css.workflowDetailsContent}>
          {activeTab === "levels" && !isDataLoading && reviewData.workflow.id && (
            <ReviewLevelTree
              reviewLevels={reviewData.workflow.reviewLevels}
              changeLevelCheckboxStatusHandler={() => {}}
              changeReviewerCheckboxStatusHandler={() => {}}
              notifyUserInReviewCallback={() => {}}
            />
          )}
          {activeTab === "diagram" && !isDataLoading && reviewData.workflow.id && (
            <TransitionsDiagram workflowId={reviewData.workflow.id} />
          )}
        </div>
      </div>
    </div>
  );

  const footer = () => (
    <div className={css.createReviewFooter}>
      <ButtonComponent
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        onClick={moveToNextSection}
        disabled={!reviewData.name?.trim() || !reviewData.workflow.id}
      >
        <Text>{t("Next")}</Text>
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeModal}>
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  return (
    <div>
      {reviewData.stage === 1 && (
        <ModalStateDialogComponent
          isVisible
          width="1000px"
          zIndex={30000}
          allowModalPadding={false}
          headerTitle={header()}
          modelContent={content()}
          footerContent={footer()}
          onCancel={closeModal}
        />
      )}

      {reviewData.stage === 2 && (
        <FileManagerAsPopup
          projectId={projectId}
          workspaceId={defaultSpaceID}
          attachSelectedBtnName="Next"
          id="fileManagerAsPopup"
          handleFileSelectedButtonClick={resourceSelectionCallback}
          handleFileSelectedCancelClick={moveToPreviousSection}
          hideIssues
          hideTasks
          hideSchedules
          hideEstimates
          hideContracts
          hideClashTests
          hideMergeFiles
          hideExternalSpace
          hideReviews
          openThisSpaceByDefault
          disableFiles={[]}
          isLoadingCreateButton={isLoadingCreateButton}
          setIsLoadingCreateButton={setIsLoadingCreateButton}
          enableFilesTypes={["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_FOLDER", "RESOURCE_TYPE_EMBED"]}
        />
      )}

      {reviewData.stage === 3 && reviewData.workflow.checklists && (
        <ChecklistAssignmentModal
          reviewData={reviewData}
          setReviewData={setReviewData}
          onNext={(updatedData: any) => {
            if (updatedData.hasIssues) {
              setIsLoadingCreateButton(false);
              setReviewData({ ...updatedData, stage: 4 });
            } else {
              setIsLoadingCreateButton(true);
              createReview(updatedData.resources);
            }
          }}
          onBack={() => setReviewData((prev: any) => ({ ...prev, stage: 2 }))}
        />
      )}

      {reviewData.stage === 4 && (
        <ModalStateDialogComponent
          isVisible
          allowModalPadding={false}
          width="500px"
          zIndex={30000}
          headerTitle={
            <p className={css.modalTitle}>
              <Text>{t("Attach Issues")}</Text>
            </p>
          }
          modelContent={
            <div className="flex flex-col justify-start gap-6">
              <p>
                <Text>{t("The file(s) that you have selected contain(s) issue(s)")}</Text>
              </p>
              <div className="flex flex-col gap-3 pe-4">
                <RadioButtonComponent
                  change={() => attachIssuesRadioBtnCallback(true)}
                  label="Attach Issues"
                  checked={!!reviewData.attachIssues}
                />
                <RadioButtonComponent
                  change={() => attachIssuesRadioBtnCallback(false)}
                  label="Don't Attach Issues"
                  checked={!reviewData.attachIssues}
                />
              </div>
            </div>
          }
          footerContent={
            <div className={css.notificationFooter}>
              <ButtonComponent
                onClick={confirmAttachIssuesCallback}
                buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
                loading={isLoadingCreateButton}
              >
                <Text>{t("Confirm")}</Text>
              </ButtonComponent>
              <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeReviewCreation}>
                <Text>{t("Cancel")}</Text>
              </ButtonComponent>
            </div>
          }
          onCancel={closeReviewCreation}
        />
      )}

      {reviewData.stage === 5 && (
        <ModalStateDialogComponent
          isVisible
          allowModalPadding={false}
          width="1000px"
          zIndex={30000}
          headerTitle={
            <p className={css.modalTitle}>
              <Text>{t("Attach Issues")}</Text>
            </p>
          }
          modelContent={
            <div className="flex flex-col h-600 relative w-full overflow-auto">
              <Issues
                showCheckboxes
                showStatusCol={false}
                disabledIssueIds={[]}
                getAllIssuesInSpace={defaultSpaceID}
                getStandaloneIssuesForSpaceId={defaultSpaceID}
                spaceId={defaultSpaceID}
                showTitleLine={false}
                allowOpenSidebar={false}
                allowTools={false}
                allowStickySidebar={false}
                issueSelectionCallback={issueSelectionCallback}
                fileVersionIds={
                  reviewData.resources
                    .filter(({ resourceType }: any) => resourceType === "RESOURCE_TYPE_FILE")
                    .map((resource: any) => resource.fileVersionId) as any
                }
              />
            </div>
          }
          footerContent={
            <div className={css.notificationFooter}>
              <ButtonComponent
                onClick={attachIssuesToReviewCallback}
                buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
                loading={isLoadingCreateButton}
              >
                <Text>{t("Attach Issues & Create Review")}</Text>
              </ButtonComponent>
              <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeReviewCreation}>
                <Text>{t("Cancel")}</Text>
              </ButtonComponent>
            </div>
          }
          onCancel={closeReviewCreation}
        />
      )}
    </div>
  );
};

export default CreateReviewModal;
